<template>
  <div class="container-tbd-chart">
    <div ref="chart" class="container-chart"></div>
    <div v-if="noData" ref="noData">
      <p style="text-align: center">NO DATA</p>
    </div>
    <div ref="zeroText" class="zeroText" style="display: none">zerotext</div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import _ from 'lodash';
import { Container, ResizeObserver, XAxis, YAxis, getTooltipTemplate, wrapText } from '../chart-services';
import { X_AXIS_CLASSNAME } from '../chart-services/x-axis';
import { renderXAxis, renderYAxis } from './axis';
import { addTooltip, getValueListTemplate } from './tooltip';

export default {
  name: 'BarChart',
  props: {
    width: {
      type: Number, default: 960
    },
    height: {
      type: Number, default: 400
    },
    autoWidth: { type: Boolean, default: false },
    isHeightByData: {
      type: Boolean, default: false
    },
    isWidthByData: {
      type: Boolean, default: true
    },
    marginLeft: {
      type: Number, default: 155
    },
    marginRight: {
      type: Number, default: 50
    },
    marginBottom: {
      type: Number, default: 50
    },
    barThickness: {
      type: Number, default: 25
    },
    xAxisPadding: {
      type: Number, default: 0.1
    },
    decimalPoint: {
      type: Number, default: 2
    },
    barOrientation: {
      // horizontal || horizontal-grouped || horizontal-stacked || vertical || grouped
      type: String, default: 'horizontal'
    },
    isXAxisFixed: {
      type: Boolean, default: false
    },
    hasYAxisGrid: {
      type: Boolean, default: false
    },
    isYAxisGridFitXAxis: {
      type: Boolean, default: false
    },
    rotateX: {
      type: Number //, default: 45
    },
    isWrapX: {
      type: Boolean, default: true
    },
    isWrapY: {
      type: Boolean, default: true
    },
    dataApi: {
      type: String //, default: 'http://localhost:8083/api/v1/format-chart-data'
    },
    apiKey: {
      type: String //, default: '298fde64234827a09f6dfccab48c17f2'
    },
    data: {
      type: String
      // , default:
      // horizontal
      // '{"status":true,"data":{"indicator":"Any type of violence against women","x_label":"Prevalence (%)","y_label":"Countries","x_unit_label":"%","tooltip":{"value":"<strong>[[internal#label]]</strong>\\r\\n<br />\\r\\n[[global#tooltip_unit_label]]: [[internal#percentage.toFixed(2)]][[global#x_unit_label]]"},"data":[{"label":"Tonga 2019","percentage":20.06,"y_val":0.07,"color":"#4979A6"},{"label":"Tuvalu 2019","percentage":33.89,"y_val":0.17,"color":"#FF6B18"},{"label":"Papua New Guinea 2018","percentage":62.94,"y_val":0.14,"color":"#FF6B18"},{"label":"Philippines 2017","percentage":24.36,"y_val":0.05,"color":"#4979A6"},{"label":"Tajikistan 2017","percentage":30.830000000000002,"y_val":0.07,"color":"#4979A6"},{"label":"Maldives 2017","percentage":23.97,"y_val":0.06,"color":"#4979A6"},{"label":"Pakistan 2017","percentage":33.47,"y_val":0.1,"color":"#FF6B18"},{"label":"Timor-Leste 2016","percentage":40.089999999999996,"y_val":0.09,"color":"#FF6B18"},{"label":"India 2016","percentage":31.03,"y_val":0.06,"color":"#4979A6"},{"label":"Armenia 2016","percentage":13.969999999999999,"y_val":0.04,"color":"#4979A6"},{"label":"Myanmar 2016","percentage":20.86,"y_val":0.05,"color":"#4979A6"},{"label":"Nepal 2016","percentage":26.33,"y_val":0.07,"color":"#4979A6"},{"label":"Afghanistan 2015","percentage":55.47,"y_val":0.09,"color":"#FF6B18"},{"label":"Cambodia 2014","percentage":28.68,"y_val":0.07,"color":"#4979A6"},{"label":"Average","percentage":31.85,"color":"#C4C4C4"}],"average_percentage":31.85,"y_avg":0.08,"sort_order":"ASC"}}'
      // horizontal-grouped
      // '{"data":{"x_label":"X Label","y_label":"Y Label","tooltip":{"value":"<strong>[[internal#label]]</strong>"},"colors":{"Not at all":"#3390ff","Not very much":"#ff3333","Medium":"#33fffc","Verfy much":"#f9ff33","Tremedously":"#ffc733"},"data":[{"label":"Student","values":{"Not at all":0,"Not very much":4,"Medium":12,"Verfy much":6,"Tremedously":0},"max_sum":12},{"label":"Liberal Profession","values":{"Not at all":1,"Not very much":21,"Medium":13,"Verfy much":18,"Tremedously":6},"max_sum":21},{"label":"Salaried Staff","values":{"Not at all":3,"Not very much":22,"Medium":6,"Verfy much":15,"Tremedously":3},"max_sum":22},{"label":"Employee","values":{"Not at all":12,"Not very much":7,"Medium":18,"Verfy much":13,"Tremedously":6},"max_sum":18}]}}'
      // vertical
      // "{\"data\":{\"x_label\":\"x axis label\",\"y_label\":\"y axis label\",\"x_unit_label\":\"\",\"tooltip_unit_label\":\"\",\"tooltip\":{\"value\":\"<strong>[[internal#label]]</strong>\"},\"data\":[{\"label\":\"Couple household, with children\",\"percentage\":100,\"color\":\"#a635ce\"},{\"label\":\"Couple household, with no children\",\"percentage\":0,\"color\":\"#77ccd9\"},{\"label\":\"One-person household, 18-59 years\",\"percentage\":0,\"color\":\"#b1e88c\"},{\"label\":\"One-person household, 60+ years\",\"percentage\":0,\"color\":\"#582430\"},{\"label\":\"Other household types\",\"percentage\":58.3,\"color\":\"#c85f0d\"},{\"label\":\"Single parent/caregiver (<60 years)\",\"percentage\":100,\"color\":\"#a8644a\"},{\"label\":\"Skipped generation\",\"percentage\":100,\"color\":\"#28f80b\"},{\"label\":\"Three generation household\",\"percentage\":100,\"color\":\"#397091\"},{\"label\":\"Total\",\"percentage\":84.5,\"color\":\"#24ebd3\"}],\"sort_order\":\"DESC\"}}"
      // grouped
      // '{"data":{"x_label":"X Label","y_label":"Y Label","tooltip":{"value":"<strong>[[internal#label]]</strong>"},"data":[{"label":"Student","values":{"Not at all":0,"Not very much":4,"Medium":12,"Verfy much":6,"Tremedously":0}},{"label":"Liberal Profession","values":{"Not at all":1,"Not very much":21,"Medium":13,"Verfy much":18,"Tremedously":6}},{"label":"Salaried Staff","values":{"Not at all":3,"Not very much":22,"Medium":6,"Verfy much":15,"Tremedously":3}},{"label":"Employee","values":{"Not at all":12,"Not very much":7,"Medium":18,"Verfy much":13,"Tremedously":6}}]}}'
    },
    filters: {
      type: String //, default: '{"node-type":"d_index","chart-type":"lollipop","country_comparison":1,"filters":{"field_year":[2014,2015,2016,2017,2018,2019,2020,2021],"field_indicator":"383","moderation_state":["published"],"field_geo":["121","132","158","223","256","273","276","289","293","296","340","343","346","352"]}}'
    },
    orderByField: {
      type: String //, default: 'percentage'
    },
    valueField: {
      type: String //, default: 'percentage'
    },
    selectedCountry: {
      type: String //, default: 'India'
    },
    xDomain: {
      type: String //, default: '[0,100]'
    },
    yDomain: {
      type: String //, default: '[0,100]'
    },
    zeroText: {
      type: String //, default: 'Note: Energy consumer is not present; energy source is not available/available but not used or not widely use in the jurisdiction for the use of renewable energy in building and infrastructure and transport sector as well as the use of energy efficient technologies in centralized energy.'
    },
    zeroLabel: {
      type: String //, default: '0*'
    },
    zeroConditionCode: {
      type: Number //, default: 1
    },
    noDataLabel: {
      type: String, default: 'N/A'
    },
    showLegend: {
      type: Boolean, default: false
    },
    showBarGroupedLabel: {
      type: Boolean, default: false
    },
    showAverageLineValueLabel: {
      type: Boolean, default: false
    },
    showTextOnBar: {
      type: Boolean, default: true
    },
    tooltipAtMousePosition: {
      type: Boolean, default: false
    },
    groupAxisData: {
      type: Array, default: undefined
    },
    averageLines: {
      type: Array
      // ,default: function () {
      //   return {
      //     xVal: 10,
      //     label: '10% Unweighted average',
      //     stroke: 'black',
      //     strokeDasharray: '8,8',
      //     strokeWidth: 3
      //   };
      // }
    }
  },
  emit: ['chartReady'],
  data: function () {
    return {
      $xAxisWrapper: null,
      margin: {
        top: 20,
        right: 50,
        bottom: 50,
        left: 155
      },
      color: {},
      vis: [],
      width_: null,
      height_: null,
      label_font_style: 'sans-serif',
      label_font_size: 13,
      noData: false,
      high_value_color: '#367AAA',
      low_value_color: '#FFD900',
      sort_order: 'DESC',
      x_unit_label: '',
      max_y_label_length: 0,
      // yLabelWidth: 0,
      is_lock: false,
      lock_target: null,
      tooltip_unit_label: 'Percentage',
      tooltip: '',
      tooltips_keys: '',
      tooltips: '',
      custom_XDomain: '',
      resizeObserver$: null,
      xScale: null,
      tipObjs: [],
      averageLineClassName: {
        wrapper: 'wrapper-average-line',
        line: 'average-line',
        wrapperText: 'wrapper-average-line-text',
        text: 'bar-line-text'
      }
    };
  },
  watch: {
    selectedNode: function (newData, oldData) {
      console.log('The selected node changed...', newData, oldData);
    },
    filters: function () {
      if (this.isNoContainerWidth()) { return; }
      this.initialize();
    },
    data: function () {
      if (this.isNoContainerWidth()) { return; }
      this.initialize();
    },
    selectedCountry: function () {
      if (this.isNoContainerWidth()) { return; }
      this.initialize();
    }
  },
  mounted: function () {
    if (this.dataApi === undefined && this.data === undefined) {
      this.setNoDataText();
      return;
    }
    this.initialize();
  },
  destroyed: function () {
    if (this.resizeObserver$ !== null) ResizeObserver.unobserve(this.resizeObserver$);
    this.tipObjs.forEach(tipObj => {
      tipObj.destroy();
    });
  },
  methods: {
    setNoDataText: function () {
      this.noData = true;
      this.$refs.chart.style.display = 'none';
    },
    hideNoDataText: function () {
      this.noData = false;
      this.$refs.chart.style.display = 'block';
    },
    setChartSize: function (data, notRecreate) {
      let dataLength = data.data.length;
      let countGroupedBar = 0;
      let countMaxInnerBar = 0;
      if (['horizontal-grouped'].includes(this.barOrientation)) {
        countGroupedBar = data.data.length;
        countMaxInnerBar = Math.max(...data.data.map(e => Object.keys(e.values).length));
        countGroupedBar = data.data.length;
        dataLength = countGroupedBar * countMaxInnerBar;
      }
      const thickness = Number(this.barThickness);
      // let minHeight = 100;
      let heightByData = (dataLength * thickness) + (dataLength * 11);
      if (['horizontal-grouped'].includes(this.barOrientation)) {
        const paddingBetweenBar = 10;
        const paddingBetweenInnerBar = 5;
        heightByData += (countGroupedBar * paddingBetweenBar) + (countMaxInnerBar * paddingBetweenInnerBar);
      }
      const marginByData = dataLength * 8;
      // this.yLabelWidth = data.y_label ? 20 : 0;
      this.margin.left = this.marginLeft; // + this.yLabelWidth;
      this.margin.right = this.marginRight;
      this.margin.bottom = this.marginBottom;

      // const maxYLabelLength = d3.max(data.data, function (d) { return d.label.length;});
      // this.max_y_label_length = maxYLabelLength;
      this.width_ = (this.autoWidth ? this.$el.offsetWidth : this.width) - this.margin.left - this.margin.right;
      // this.height_ = this.height - this.margin.top - this.margin.bottom;
      this.height_ = heightByData - marginByData;
      if (this.isHeightByData === false) {
        this.height_ = this.height;
      }
      // this.height_ = this.height_ < minHeight ? minHeight : this.height_;

      // Set bar chart color
      this.high_value_color = data.high_value_color ? data.high_value_color : this.high_value_color;
      this.low_value_color = data.low_value_color ? data.low_value_color : this.low_value_color;
      // Set sortOrder
      this.sort_order = data.sort_order ? data.sort_order : this.sort_order;
      // Set Unit label
      this.x_unit_label = data.x_unit_label ? data.x_unit_label : this.x_unit_label;
      // Set tooltip label
      this.tooltip_unit_label = data.tooltip_unit_label ? data.tooltip_unit_label : this.tooltip_unit_label;

      if (notRecreate !== true) {
        Container.removeContainer(this.$refs.chart);
      }
      const containerInfo = Container.renderContainer(this.$refs.chart, this.width_, this.height, this.height_, this.margin, {
        isXAxisFixed: this.isXAxisFixed,
        hasXAxisLabel: data.x_label !== undefined,
        transitionDuration: 1000
      });
      this.vis[0] = containerInfo.$chartWrapper;
      this.$xAxisWrapper = containerInfo.$xAxisWrapper;
    },
    fetchData: async function () {
      this.hideNoDataText();
      let json;
      if (this.data !== undefined) {
        json = JSON.parse(this.data);
        // Mock negative data
        // json.data.data = json.data.data.map(e => {
        //   // e.percentage = -(e.percentage)
        //   // return { ...e };
        //   // horizontal-grouped
        //   const res = { ...e };
        //   for (const key of Object.keys(res.values)) {
        //     res.values[key] = res.values[key] === 0 ? 0 : -(res.values[key]);
        //   }
        //   res.max_sum = res.max_sum === 0 ? 0 : -(res.max_sum);
        //   return res;
        // });
      }
      if (this.dataApi !== undefined) {
        json = await d3.json(this.dataApi, {
          method: 'POST',
          body: this.filters,
          headers: {
            'Content-type': 'application/json; charset=UTF-8',
            'api-key': this.apiKey
          },
          credentials: 'omit'
        });
      }

      if (json && json.data && json.data.data && json.data.data.length > 0) {
        this.tooltip = json.data.tooltip.value;
        if (json.data.tooltips_keys) {
          this.tooltips_keys = json.data.tooltips_keys;
          this.tooltips = json.data.tooltip;
        }
        if (json.data.custom_xdomain) {
          this.custom_XDomain = '[' + json.data.custom_xdomain[0] + ',' + json.data.custom_xdomain[1] + ']';
        }
        if (['horizontal', 'horizontal-grouped', 'horizontal-stacked'].includes(this.barOrientation) === true) {
          this.setChartSize(json.data);
          this.renderChart(json.data.data, json.data.colors, json.data.x_label, json.data.y_label, json.data.average_percentage);
        } else if (this.barOrientation === 'grouped') {
          this.setChartSize(json.data);
          this.renderChartGrouped(json.data.data, json.data.colors, json.data.x_label, json.data.y_label);
        } else {
          this.setChartSize(json.data, true);
          this.renderChartVertical(json.data.data, json.data.x_label, json.data.y_label);
        }
        if (this.autoWidth === true && this.resizeObserver$ === null) {
          const resizeObserverOptions = { onlyWidth: true, skipFirst: true };
          if (this.isNoContainerWidth()) {
            // If width less than zero, it will not trigger in the first time
            resizeObserverOptions.skipFirst = false;
          }
          this.resizeObserver$ = ResizeObserver.observe(this.$el, () => {
            if (this.isNoContainerWidth()) { return; }
            this.initialize();
          }, resizeObserverOptions);
        }
        this.hideNoDataText();
        this.$emit('chartReady', this);
      } else {
        this.setNoDataText();
      }
    },
    renderChart: function (data, colors, xLabel, yLabel, avg = null) {
      function fmtData () {
        // if (this.barOrientation === 'horizontal-grouped') {
        //   data.sort((a, b) => {
        //     const labelA = a.label.toLowerCase();
        //     const labelB = b.label.toLowerCase();
        //     if (labelA < labelB) { return 1; }
        //     if (labelA > labelB) { return -1; }
        //     return 0; // labels are equal
        //   });
        // }
        if (this.barOrientation === 'horizontal-stacked') {
          data = data.map(d => {
            const _d = { ...d, ...d.values };
            return _d;
            // Object.keys(_d.values).forEach(key => _d.values[key] = Math.abs(_d.values[key]));
            // return { ..._d, ..._d.values };
          });
        }
        if (self.orderByField === undefined) { return; }
        data.sort(function (a, b) {
          if (self.sort_order === 'ASC') {
            return d3.ascending(a[self.orderByField], b[self.orderByField]);
          } else {
            return d3.descending(a[self.orderByField], b[self.orderByField]);
          }
        });
      }
      function getBarTextX (x, value) {
        const barValPadding = value < 0 ? -5 : 5;
        return x(value) + barValPadding;
      }
      function getBarTextY (y, value) {
        const yPadding = 5;
        return y(value) + (y.bandwidth() / 2) + yPadding;
      }

      const self = this;
      const width = this.width_ ? this.width_ : this.width;
      const height = this.height_ ? this.height_ : this.height;
      const thickness = Number(this.barThickness);
      const xAxisHeight = 20;

      let keys = [];
      if (['horizontal-grouped', 'horizontal-stacked'].includes(this.barOrientation)) {
        for (const item of data) { keys.push(...Object.keys(item.values)); }
        keys = _.uniq(keys);
      }

      fmtData.call(this);

      const y = YAxis.getYScale([height, 0], data.map(d => d.label));
      let yInner; // colorMapping z;
      if (this.barOrientation === 'horizontal-grouped') {
        yInner = YAxis.getYScale([0, y.bandwidth()], keys);
      }

      const xDomain = [];
      const customXDomain = this.xDomain ? JSON.parse(this.xDomain) : (this.custom_XDomain ? JSON.parse(this.custom_XDomain) : null);
      let xMin = 0;
      let xMax = 0;
      let xStart = 0;
      if (['horizontal-grouped'].includes(this.barOrientation)) {
        xMin = d3.min(data, d => d3.min(keys, key => d.values[key]));
        xMax = d3.max(data, d => d3.max(keys, key => d.values[key]));
      } else if (['horizontal-stacked'].includes(this.barOrientation)) {
        const totalStackedData = data.map(d => {
          const _d = { ...d };
          _d.total = keys.reduce((total, key) => {
            return total + _d.values[key];
          }, 0);
          return _d;
        });
        xMin = d3.min(totalStackedData, d => d.total);
        xMax = d3.max(totalStackedData, d => d.total);
        if (xMax > 0) {
          xMin = 0; // Stacked from zero
        }
      } else {
        xMin = d3.min(data, e => e.percentage);
        xMax = d3.max(data, e => e.percentage);
      }

      // Note: comment the follwing logic in web component. The custom domain will come from the API
      // Add 10% of difference between min and max in x-axis
      // const diff10 = Math.abs(xMax - xMin) * 0.1;
      // xMin = (xMin === 0) ? Math.max(xMin - diff10, 0) : xMin - diff10;
      // xMax = (xMax === 100) ? Math.min(xMax + diff10, 100) : xMax + diff10;

      if (xMax === 0 && xMin === 0) {
        xDomain.push(xMin);
        xDomain.push(xMax);
      } else if (xMax <= 0) {
        xStart = 0.0001; // @NOTE: Not sure why can't use 0
        xDomain.push(xMin);
        xDomain.push(xStart);
      } else if (xMin >= 0) {
        xStart = xMin;
        xDomain.push(xMin);
        xDomain.push(xMax);
      } else if (xMin <= 0 && xMax > 0) {
        xStart = 0.0001;
        xDomain.push(xMin);
        xDomain.push(xMax);
      }
      if (customXDomain !== null) {
        xDomain[0] = customXDomain[0];
        xDomain[1] = customXDomain[1];
        if (xMin >= 0) {
          xStart = customXDomain[0];
        }
      }
      this.xScale = XAxis.getXScale([0, width], xDomain);
      // X Axis
      const xAxis = XAxis.getXAxis(this.xScale, { tickSizeInner: -height });
      renderXAxis.call(this, this.xScale, xAxis, xLabel);
      // END:X Axis

      // Y Axis
      const yAxis = YAxis.getYAxis(y);
      const yStart = xStart;
      let customTranslateX = xMin < 0 ? -this.xScale(yStart) : this.xScale(xDomain[1]);
      if (xMin <= 0 && xMax > 0) {
        customTranslateX = this.xScale(yStart);
      }
      const yRenderOptions = {
        translate: { x: this.xScale(yStart), y: 0 },
        showBarGroupedTranslate: { x: customTranslateX },
        showBarGroupedLabel: this.showBarGroupedLabel
      };
      renderYAxis.call(this, y, yAxis, yLabel, yRenderOptions);
      const $chart = d3.select(this.$refs.chart);
      const tickXPadding = 9;
      $chart.selectAll('.y-axis .tick').data(data)
        .select('text')
        .attr('class', d => {
          if (d === undefined) return;
          if (d.label.indexOf(this.selectedCountry) > -1) {
            return 'selected-country';
          }
        })
        .attr('x', d => {
          const value = ['horizontal-grouped', 'horizontal-stacked'].includes(this.barOrientation)
            ? d.max_sum
            : d.percentage;
          let tempPadding = value < xStart ? tickXPadding : -(tickXPadding);
          // if (xMin <= 0 && xMax > 0 && this.showBarGroupedLabel) {
          //   tempPadding = -(tickXPadding);
          // }
          if (this.showBarGroupedLabel) {
            tempPadding = value < xStart ? -2 : 2;
          }
          if (xMin >= 0 && xMax > 0 && value === 0) {
            tempPadding = -(tickXPadding);
          }
          return tempPadding;
        })
        .style('text-anchor', d => {
          const value = ['horizontal-grouped', 'horizontal-stacked'].includes(this.barOrientation)
            ? d.max_sum
            : d.percentage;
          let tempAnchor = value < xStart ? 'start' : 'end';
          if (xMin <= 0 && xMax > 0 && this.showBarGroupedLabel) {
            tempAnchor = 'end';
          }
          if (xMin >= 0 && xMax > 0 && value === 0) {
            tempAnchor = 'end';
          }
          // Special case by design
          if (this.showBarGroupedLabel) {
            if (tempAnchor === 'start') {
              tempAnchor = 'end';
            } else {
              tempAnchor = 'start';
            }
          }
          return tempAnchor;
        });
      // @Todo : hide tick and label in this case for temporary
      // if (this.barOrientation === 'horizontal-grouped' && xMin < 0 && xMax > 0) {
      //   $chart
      //     .selectAll('.y-axis .tick')
      //     .attr('display', 'none');
      //   $chart
      //     .selectAll('.y-axis .y-axis-label')
      //     .attr('display', 'none');
      // }
      // END: Y Axis

      const barClassName = 'bar';
      const tickClassName = '.y-axis .tick';
      const textChildClassName = 'bar-text-child';
      const chartData = this.barOrientation === 'horizontal-stacked'
        ? d3.stack().keys(keys)(data)
        : data;
      const nodeEnter = this.vis[0].selectAll(`.${barClassName}`).data(chartData).enter();
      let tooltipTemplateCb = null;

      if (this.barOrientation === 'horizontal-grouped') {
        const xScaleStart = this.xScale(xStart);
        nodeEnter.append('g')
          .attr('class', barClassName)
          .attr('transform', d => {
            return 'translate(0, ' + y(d.label) + ')';
          })
          .selectAll('rect')
          .data(d => {
            return keys.map(key => {
              const tempObj = {
                key: key,
                value: d.values[key] || d.values[key] === 0 ? d.values[key] : null,
                group: d.label,
                ...d
              };
              return tempObj;
            });
          })
          .enter().append('rect')
          .attr('x', d => {
            return this.xScale(Math.min(xStart, d.value));
          })
          .attr('y', d => {
            return this.isHeightByData ? yInner(d.key) : yInner(d.key) + yInner.bandwidth() / 2 - (thickness / 2);
          })
          .attr('height', thickness)
          .attr('width', d => {
            // const val = this.xScale(d.value);
            // return val < 0 ? 0 : val;
            const tempWidth = d.value ? Math.abs(this.xScale(d.value) - xScaleStart) : 0;
            return tempWidth;
          })
          .attr('class', d => {
            if (self.tooltips_keys) {
              return self.tooltips_keys[d.key];
            }
            return '';
          })
          .attr('fill', d => {
            return colors[d.key];
          });

        if (this.averageLines) {
          this.renderAverageLines();
        }

        // Bar value
        nodeEnter
          .append('g')
          .attr('class', 'bar-text-wrap')
          .attr('transform', d => {
            return 'translate(0,' + y(d.label) + ')';
          })
          .selectAll('text')
          .data(d => {
            return keys.map(key => {
              const returnObj = {
                key: key,
                value: d.values[key] || d.values[key] === 0 ? d.values[key] : null,
                group: d.label,
                max_sum: d.max_sum ? d.max_sum[key] : undefined,
                ...d
              };
              return returnObj;
            });
          })
          .enter()
          .append('text')
          .attr('class', 'bar-text')
          .attr('x', d => {
            let tempVal = d.value ? d.value : xMin;
            if (!d.value && xMin < 0) {
              tempVal = -0.01;
            }
            if (d.value === 0) {
              tempVal = 0;
            }
            return getBarTextX(this.xScale, tempVal);
          })
          .attr('y', d => {
            return getBarTextY(yInner, d.key);
          })
          .style('text-anchor', d => {
            let anchor = d.value && d.value < 0 ? 'end' : 'start';
            if (!d.value && xMin < 0) {
              anchor = 'end';
            }
            if (d.value === 0) {
              anchor = 'start';
            }
            return anchor;
          })
          .attr('fill', 'black')
          .text(d => {
            let displayValue = (d.value !== undefined) ? parseFloat(d.value).toFixed(this.decimalPoint) : this.noDataLabel;
            // @Note: not to show NaN on bars' labels
            if (displayValue === 'NaN') {
              displayValue = '';
            }
            if ((this.zeroLabel && d.value === 0) || (displayValue === '0.0')) {
              displayValue = this.zeroLabel;
            }
            if (this.x_unit_label) {
              displayValue += this.x_unit_label;
            }
            // console.log(d);
            if (d.customized_labels && d.customized_labels[d.key]) {
              displayValue = d.customized_labels[d.key];
            }
            return displayValue;
          });

        if (this.showBarGroupedLabel) {
          // Bar children label
          nodeEnter
            .append('g')
            .attr('class', 'bar-text-child')
            .attr('transform', d => {
              return 'translate(0,' + y(d.label) + ')';
            })
            .selectAll('text')
            .data(d => {
              return keys.map(key => {
                const returnObj = {
                  key: key,
                  value: d.values[key] ? d.values[key] : null,
                  group: d.label,
                  max_sum: d.max_sum ? d.max_sum[key] : undefined
                };
                return returnObj;
              });
            })
            .enter()
            .append('text')
            .attr('class', 'bar-text')
            .attr('x', d => {
              let tempVal = -0.000001;
              if (d.value < 0) {
                tempVal = 0.000001;
              }
              if (!d.value) {
                tempVal = 0.000001;
              }
              return getBarTextX(this.xScale, tempVal);
            })
            .attr('y', d => {
              return getBarTextY(yInner, d.key);
            })
            .style('text-anchor', d => {
              let anchor = 'end';
              if (!d.value) {
                anchor = 'start';
              }
              if (d.value < 0) {
                anchor = 'start';
              }
              return anchor;
            })
            .text(d => {
              return d.key;
            });
        }

        if (this.tooltip === undefined) {
          tooltipTemplateCb = function (event, d) {
            return `<strong>${d.label}</strong>${getValueListTemplate(d.values, self.decimalPoint, colors)}`;
          };
        } else {
          tooltipTemplateCb = function (event, d) {
            return eval(`\`${getTooltipTemplate(self.tooltip, 'd', 'self')}\``); // eslint-disable-line no-eval
          };
        }
      } else if (this.barOrientation === 'horizontal-stacked') {
        const isNegativeValueChart = this.xScale.domain()[0] < 0;
        nodeEnter.append('g')
          .attr('fill', d => {
            return colors[d.key];
          })
          .selectAll('rect')
          .data(d => d)
          .enter().append('rect')
          .attr('class', barClassName)
          .attr('x', d => {
            const val = isNegativeValueChart ? Math.min(xStart, d[1]) : d[0];
            return this.xScale(val);
          })
          .attr('y', d => {
            const label = d.data.label;
            return y(label);
          })
          .attr('width', d => {
            const width = isNegativeValueChart
              ? this.xScale(d[1]) - this.xScale(d[0])
              : this.xScale(d[0]) - this.xScale(d[1]);
            return Math.abs(width);
          })
          .attr('height', y.bandwidth());
        if (this.tooltip === undefined) {
          tooltipTemplateCb = function (e, d) {
            if (d.data !== undefined) {
              return `<strong>${d.data.label}</strong>${getValueListTemplate(d.data.values, this.decimalPoint, colors)}`;
            }
            return `<strong>${d.label}</strong>${getValueListTemplate(d.values, self.decimalPoint, colors)}`;
          };
        } else {
          // console.log(self.tooltip);
          tooltipTemplateCb = function (event, d) {
            // console.log(d);
            if (d.data !== undefined) {
              const dd = d.data;
              // console.log(dd);
              return eval(`\`${getTooltipTemplate(self.tooltip, 'dd', 'self')}\``); // eslint-disable-line no-eval
            }
            return eval(`\`${getTooltipTemplate(self.tooltip, 'd', 'self')}\``); // eslint-disable-line no-eval
          };
        }
      } else {
        nodeEnter.append('rect')
          .attr('class', barClassName)
          .attr('width', d => {
            // return x(d.percentage);
            return Math.abs(this.xScale(d.percentage) - this.xScale(xStart));
          })
          .attr('x', d => this.xScale(Math.min(xStart, d.percentage)))
          .attr('y', function (d) {
            return this.isHeightByData ? y(d.label) : y(d.label) + y.bandwidth() / 2 - (thickness / 2);
          })
          .attr('height', thickness)
          .attr('fill', function (d) {
            return d.color;
            // if (avg && d.percentage < avg) return this.low_value_color;
            // else return this.high_value_color;
          });

        if (this.averageLines) {
          this.renderAverageLines();
        }

        // Bar value
        nodeEnter.append('text')
          .attr('class', 'bar-text')
          .attr('x', d => {
            return getBarTextX(this.xScale, d.percentage);
          })
          .attr('y', d => {
            return getBarTextY(y, d.label);
          })
          .style('text-anchor', 'start')
          .attr('fill', d => {
            if (avg && d.percentage < avg) return 'black';
            else return 'black';
          })
          .text(d => {
            let displayValue = d.percentage.toFixed(this.decimalPoint) + this.x_unit_label;
            if (this.zeroLabel && d.percentage === 0) {
              if (this.zeroConditionCode === 1) {
                displayValue = this.zeroLabel;
              } else if (this.zeroConditionCode === 2 && d.city_score === 0 && d.max_score === 0) {
                displayValue = this.zeroLabel;
              } else if (this.zeroConditionCode === 3) {
                if (d.city_score === 0 && d.max_score === 0) {
                  displayValue = this.zeroLabel;
                }
                if (d.is_slum === 3) {
                  displayValue = '0**';
                }
                if (d.is_ignore === 1) {
                  displayValue = this.zeroLabel;
                }
              } else {
                displayValue = d.percentage.toFixed(this.decimalPoint) + this.x_unit_label;
              }
            }
            return displayValue;
          });

        tooltipTemplateCb = function (event, d) {
          return eval(`\`${getTooltipTemplate(self.tooltip, 'd', 'self')}\``); // eslint-disable-line no-eval
        };
      }

      const tooltipMultipleTemplateCb = function (event, d) {
        // console.log(d);
        return eval(`\`${getTooltipTemplate(self.tooltips[self.tooltips_keys[d.key]], 'd', 'self')}\``); // eslint-disable-line no-eval
      };
      if (this.tooltips_keys) {
        let tipBarHoverByKey;
        for (const tkk in this.tooltips_keys) {
          const tk = this.tooltips_keys[tkk];
          tipBarHoverByKey = addTooltip.call(this, this.vis[0], this.vis[0].selectAll(`.${tk}`), tooltipMultipleTemplateCb, 'bar-hover');
          this.tipObjs.push(tipBarHoverByKey);
        }
      } else {
        const tipBarHover = addTooltip.call(this, this.vis[0], this.vis[0].selectAll(`.${barClassName}`), tooltipTemplateCb, 'bar-hover');
        this.tipObjs.push(tipBarHover);
      }
      const tipTickHover = addTooltip.call(this, this.vis[0], this.vis[0].selectAll(`${tickClassName}`), tooltipTemplateCb, 'tick-hover');
      this.tipObjs.push(tipTickHover);
      if (this.barOrientation === 'horizontal-grouped' && this.showBarGroupedLabel) {
        const tipTickHover2 = addTooltip.call(this, this.vis[0], this.vis[0].selectAll(`.${textChildClassName}`), tooltipTemplateCb, 'tick-hover');
        this.tipObjs.push(tipTickHover2);
      }

      let allVal = data.map(d => d.percentage);
      let maxScores = data.map(d => d.max_score);
      if (this.barOrientation === 'horizontal-grouped') {
        allVal = data.map(d => d.values).flatMap(d => Object.values(d));
        maxScores = data.filter(d => d.max_sum).map(d => d.max_sum).flatMap(d => Object.values(d));
      }
      let hasZero = allVal.includes(0);
      if (this.zeroConditionCode && this.zeroConditionCode !== 1 && maxScores.length) {
        hasZero = maxScores.includes(0);
      }

      // const allSlum = data.map(d => d.is_slum);
      const isNoSlum = allVal.includes(3);

      // Zero text
      if (this.zeroText && hasZero && this.zeroConditionCode) {
        const zeroTextEl = this.$refs.zeroText;
        zeroTextEl.style.display = 'block';
        zeroTextEl.style.width = this.width + 'px';
        let tempText = this.zeroText;
        if (isNoSlum) {
          tempText += '\r\n**There are no slums in the jurisdiction.';
        }
        zeroTextEl.textContent = tempText;
        zeroTextEl.innerHTML = zeroTextEl.innerHTML.replace(/\n\r?/g, '<br />');
      }

      if (this.showLegend === true) {
        const spaceBetweenComponentX = 20;
        const legendContainerTopPos = height + xAxisHeight + spaceBetweenComponentX;
        const legendContainer = this.vis[0].append('g').attr('class', 'legend-container')
          .attr('font-family', 'sans-serif')
          .attr('font-size', 10)
          .attr('transform', () => 'translate(0, ' + legendContainerTopPos + ')');
        const legendItems = legendContainer.selectAll('.legend-item')
          .data(keys).enter()
          .append('g')
          .attr('class', 'legend-item')
          .attr('text-anchor', 'start')
          .attr('transform', function (d, i) { return 'translate(0,' + (i * 20) + ')'; })
          .attr('fill', '#383838');
        legendItems.append('rect')
          .attr('x', 0)
          .attr('width', 15)
          .attr('height', 15)
          .attr('fill', d => {
            return colors[d];
          })
          .attr('stroke', d => {
            return colors[d];
          })
          .attr('stroke-width', 2);
        legendItems.append('text')
          .attr('x', 24)
          .attr('y', 9.5)
          .attr('dy', '0.32em')
          .text(function (d) { return d; });
        const svgEle = this.vis[0].node().parentNode;
        const svgHeight = svgEle.getBoundingClientRect().height;
        svgEle.setAttribute('height', (svgHeight +
          spaceBetweenComponentX +
          legendContainer.node().getBoundingClientRect().height)); // Adjust SVG height
      }
    },
    renderChartVertical: function (data, xLabel, yLabel) {
      const transitionDuration = 1000;
      const valueField = this.valueField !== undefined ? this.valueField : 'percentage';

      // X Axis
      const xDomain = this.xDomain === undefined
        ? data.map(function (d) { return d.label; })
        : JSON.parse(this.xDomain);
      const xRange = [0, this.width_];
      const xScale = XAxis.getXScale(xRange, xDomain, { scaleType: 'scaleBand', padding: this.xAxisPadding });
      this.xScale = xScale;
      const xAxis = XAxis.getXAxis(this.xScale);
      renderXAxis.call(this, this.xScale, xAxis, xLabel, transitionDuration);
      // END: X Axis

      /* Group Axis */
      if (this.groupAxisData) {
        const chartGroups = [];
        this.groupAxisData.forEach(function (d, i, array) {
          chartGroups.push({
            group: d.name,
            order: d.order,
            length: d.data.length,
            start: xScale(d.start),
            end: xScale(d.end)
          });
        });
        // this.vis[0].selectAll('.wrapper-chart-groups').remove();
        const $xAxis = this.vis[0].select(`.${X_AXIS_CLASSNAME}`);
        const xAxisHeight = $xAxis.empty() ? 0 : $xAxis.node().getBBox().height;
        const spacing = 5;
        const chartGroupWrapper = this.vis[0]
          .selectAll('.wrapper-chart-groups')
          .data([1])
          .enter()
          .append('g')
          .attr('class', 'wrapper-chart-groups')
          .attr('transform', `translate(0, ${(this.height_ + (xAxisHeight ? xAxisHeight + spacing : 0))})`);
        const chartGroup = chartGroupWrapper
          .selectAll('.chart-group')
          .data(chartGroups)
          .enter()
          .append('g')
          .attr('class', 'chart-group');
        chartGroup.append('text')
          .attr('x', d => {
            const charWidth = 2;
            const textWidth = (d.group.length * charWidth);
            if (textWidth > d.end) return (d.start + d.end) / 2;
            return (d.start + d.end + textWidth) / 2;
          })
          .attr('y', '1em')
          .attr('text-anchor', 'middle')
          .attr('fill', '#8f8f8f')
          .attr('font-size', '11px')
          .attr('data-width', d => {
            const minWidth = 35;
            const width = d.end - d.start;
            return width > minWidth ? width : minWidth;
          })
          .text(d => d.group)
          .call(wrapText, 'data-width');
        chartGroup.append('line')
          .style('stroke', '#8f8f8f')
          .style('stroke-width', (d, i) => {
            return i > 0 ? '1px' : '0px';
          })
          .attr('x1', d => {
            return d.start;
          })
          .attr('y1', 0)
          .attr('x2', d => {
            return d.start;
          })
          .attr('y2', 25);
      } /* /Group Axis */

      // Y Axis
      let isNegativeValueChart = false;
      let yDomain = [];
      let yStart = 0;
      let yMin = 0;
      let yMax = 0;
      const yCustomDomain = this.yDomain !== undefined ? JSON.parse(this.yDomain) : [];
      if (yCustomDomain.length > 0) {
        yDomain = [yCustomDomain[0], yCustomDomain[yCustomDomain.length - 1]];
        if (yDomain[0] < 0) {
          isNegativeValueChart = true;
          yStart = yDomain[1];
        } else {
          yStart = yDomain[0];
        }
        yMin = yDomain[0];
        yMax = yDomain[1];
      } else {
        yMin = d3.min(data, d => d[valueField]);
        yMax = d3.max(data, d => d[valueField]);
        if (yMin < 0) {
          isNegativeValueChart = true;
          yMax = yStart;
        } else {
          yMin = yStart;
        }
        yDomain.push(yMin, yMax);
      }
      const yRange = [this.height_, 0];
      this.yScale = YAxis.getYScale(yRange, yDomain, { scaleType: 'scaleLinear' });
      const yAxisOptions = {};
      if (yCustomDomain.length > 2) {
        yAxisOptions.customDomain = yCustomDomain;
      }
      const yAxis = YAxis.getYAxis(this.yScale, yAxisOptions);
      const yRenderOptions = {
        translate: { x: this.xScale(yStart), y: 0 },
        transitionDuration
      };
      renderYAxis.call(this, this.yScale, yAxis, yLabel, yRenderOptions);
      // renderYAxis.call(this, this.yScale, yAxis, yLabel);
      // END: Y Axis

      const barGroupClassname = 'bar-group';
      const joinedBarGroup = this.vis[0].selectAll(`.${barGroupClassname}`).data(data);
      // const nodeEnter = this.vis[0].selectAll(`.${barClassName}`).data(data).enter();
      const renderBar = (bar) => {
        bar
          .attr('x', d => {
            return this.xScale(d.label);
          })
          .attr('width', d => {
            return this.xScale.bandwidth();
          })
          .attr('y', d => {
            if (isNegativeValueChart) {
              return this.yScale(yStart);
            }
            return this.yScale(d[valueField]);
          })
          .attr('height', d => {
            if (isNegativeValueChart) {
              return Math.abs(this.yScale(d[valueField]));
            }
            return Math.abs(this.height_ - this.yScale(d[valueField]));
          })
          .attr('fill', d => {
            return d.color;
          });
      };
      const barGroupEnter = joinedBarGroup
        .join('g')
        .attr('class', barGroupClassname);
      joinedBarGroup
        .exit()
        .transition().duration(transitionDuration)
        .remove();
      const barGroup = barGroupEnter.merge(joinedBarGroup);
      const barClassname = 'bar';
      const joinedBar = barGroup.selectAll(`.${barClassname}`).data(d => [d]);
      joinedBar
        .enter()
        .append('rect')
        .attr('class', barClassname)
        .call(renderBar);
      joinedBar
        .transition().duration(transitionDuration)
        .call(renderBar);
      joinedBar
        .exit()
        .transition().duration(transitionDuration)
        .remove();

      if (this.showTextOnBar) {
        const renderBarText = text => {
          text
            .attr('x', d => {
              return this.xScale(d.label) + this.xScale.bandwidth() / 2;
            })
            .style('text-anchor', 'middle')
            .attr('y', d => {
              if (isNegativeValueChart) {
                return this.yScale(d[valueField]);
              }
              const spacing = 12; // 12 is font size
              return this.yScale(d[valueField]) + spacing;
            })
            .text(d => {
              return d[valueField].toFixed(this.decimalPoint);
            });
        };
        const classname = 'bar-text';
        const joinedBarText = barGroup.selectAll(`.${classname}`).data(d => [d]);
        joinedBarText
          .enter()
          .append('text')
          .attr('class', classname)
          .call(renderBarText);
        joinedBarText
          .transition().duration(transitionDuration)
          .call(renderBarText);
        joinedBarText
          .exit()
          .transition().duration(transitionDuration)
          .remove();
      }

      let tooltipTemplateCb;
      if (this.tooltip === undefined) {
        tooltipTemplateCb = function (event, d) {
          const tempVal = d[valueField] ? d[valueField].toFixed(this.decimalPoint) : this.noDataLabel;
          return '<strong>' + d.label + '</strong><br>' + 'Value' + ': ' + tempVal;
        };
      } else {
        tooltipTemplateCb = (event, d) => {
          return eval(`\`${getTooltipTemplate(this.tooltip, 'd', 'self')}\``); // eslint-disable-line no-eval
        };
      }

      const tipBarHover = addTooltip.call(this, this.vis[0], this.vis[0].selectAll(`.${barClassname}`), tooltipTemplateCb, 'bar-hover');
      this.tipObjs.push(tipBarHover);

      if (this.averageLines) {
        const $wrapperAverageLine = this.vis[0].append('g')
          .attr('class', this.averageLineClassName.wrapper);
        for (const averageLine of this.averageLines) {
          const yVal = this.yScale(averageLine.val);
          $wrapperAverageLine.append('line')
            .attr('data-label', averageLine.label ? averageLine.label : '')
            .attr('data-value', averageLine.val)
            .attr('class', this.averageLineClassName.line)
            .attr('x1', 0)
            .attr('x2', this.width_)
            .attr('y1', yVal)
            .attr('y2', yVal)
            .attr('stroke', averageLine.stroke)
            .attr('stroke-dasharray', averageLine.strokeDasharray)
            .attr('stroke-width', averageLine.strokeWidth);

          if (this.showAverageLineValueLabel) {
            const $wrapperAverageLineText = this.vis[0].append('g')
              .attr('class', this.averageLineClassName.wrapperText);
            $wrapperAverageLineText.append('text')
              .attr('class', this.averageLineClassName.text)
              .attr('x', d => {
                return 10;
              })
              .attr('y', d => {
                return yVal;
              })
              .attr('text-anchor', 'middle')
              .attr('dy', '1.6em')
              .attr('fill', 'black')
              .text(d => {
                return `${averageLine.val}`;
              });
          }

          if (averageLine.label) {
            const tipLineHover = addTooltip.call(this, this.vis[0], this.vis[0].selectAll('.average-line'), function (event, d) {
              const lineLabel = event.currentTarget.getAttribute('data-label');
              const lineValue = event.currentTarget.getAttribute('data-value');
              return `${lineLabel} : ${lineValue}`;
            }, 'line-hover');
            this.tipObjs.push(tipLineHover);
          }
        }
      }
    },
    renderChartGrouped: function (data, colors, xLabel, yLabel) {
      const self = this;
      // const thickNess = Number(self.barThickness);

      // X Axis
      const x0 = XAxis.getXScale([0, this.width_], data.map(function (d) { return d.label; }), { scaleType: 'scaleBand' });
      const keys = Object.keys(data[0].values);
      const customXDomain = this.xDomain ? JSON.parse(this.xDomain) : null;
      const x1 = XAxis.getXScale(customXDomain || [0, x0.bandwidth()], keys, { scaleType: 'scaleBand', padding: 0.1 });
      const xAxis0 = XAxis.getXAxis(x0);
      renderXAxis.call(this, x0, xAxis0, xLabel);
      // END: X Axis

      // Y Axis
      const y = YAxis.getYScale([this.height_, 0],
        [0, d3.max(data, function (d) { return d3.max(keys, function (key) { return d.values[key]; }); })],
        { scaleType: 'scaleLinear' });
      const yAxis = YAxis.getYAxis(y);
      renderYAxis.call(this, y, yAxis, yLabel);
      // END: Y Axis

      const barClassName = 'bar';
      const nodeEnter = self.vis[0].selectAll(`.${barClassName}`).data(data).enter();

      const z = d3.scaleOrdinal()
        .range(['#11ba4f', '#fa6c00', '#fa4150', '#872175', '#01b4e7']);
      nodeEnter.append('g')
        .attr('class', barClassName)
        .attr('transform', function (d) { return 'translate(' + x0(d.label) + ',0)'; })
        .selectAll('rect')
        .data(function (d) { return keys.map(function (key) { return { key: key, value: d.values[key] }; }); })
        .enter().append('rect')
        .attr('x', function (d) {
          // return x1(d.key) + x1.bandwidth() / 2 - (thickNess / 2);
          return x1(d.key);
        })
        .attr('y', function (d) { return y(d.value); })
        .attr('width', function (d) {
          // return thickNess;
          return x1.bandwidth();
        })
        .attr('height', (d) => {
          return this.height_ - y(d.value);
        })
        .attr('fill', function (d) { return z(d.key); });

      nodeEnter
        .append('g')
        .attr('class', 'bar-text-wrap')
        .attr('transform', function (d) { return 'translate(' + x0(d.label) + ',0)'; })
        .selectAll('text')
        .data(function (d) { return keys.map(function (key) { return { key: key, value: d.values[key] }; }); })
        .enter().append('text')
        .attr('class', 'bar-text')
        .attr('x', function (d) {
          return x1(d.key) + x1.bandwidth() / 2;
        })
        .style('text-anchor', 'middle')
        .attr('y', function (d) {
          return y(d.value) - 5;
        })
        .text(function (d) {
          return typeof d.value === 'number' ? d.value.toFixed(self.decimalPoint) : null;
        });

      const tipBarHover = addTooltip.call(this, this.vis[0], this.vis[0].selectAll(`.${barClassName}`), function (event, d) {
        return `<strong>${d.label}</strong>${getValueListTemplate(d.values, self.decimalPoint, colors)}`;
      }, 'bar-hover');
      this.tipObjs.push(tipBarHover);

      // Legend
      // const legend = nodeEnter.append('g')
      //   .attr('font-family', 'sans-serif')
      //   .attr('font-size', 10)
      //   .attr('text-anchor', 'end')
      //   .selectAll('g')
      //   .data(keys.slice().reverse())
      //   .enter().append('g')
      //   .attr('transform', function (d, i) { return 'translate(0,' + i * 20 + ')'; });
      // legend.append('rect')
      //   .attr('x', self.width - 17)
      //   .attr('width', 15)
      //   .attr('height', 15)
      //   .attr('fill', z)
      //   .attr('stroke', z)
      //   .attr('stroke-width', 2);
      // // .on("click",function(d) { update(d) });
      // legend.append('text')
      //   .attr('x', self.width - 24)
      //   .attr('y', 9.5)
      //   .attr('dy', '0.32em')
      //   .text(function (d) { return d; });
      // END: Legend
    },
    isNoContainerWidth: function () {
      return this.$el && this.$el.offsetWidth <= 0;
    },
    renderAverageLines: function () {
      function getBarTextX (x, value) {
        const barValPadding = value < 0 ? -5 : 5;
        return x(value) + barValPadding;
      }
      const $wrapperAverageLine = this.vis[0].append('g')
        .attr('class', this.averageLineClassName.wrapper);
      for (const averageLine of this.averageLines) {
        const x = this.xScale(averageLine.xVal);
        $wrapperAverageLine.append('line')
          .attr('data-label', averageLine.label ? averageLine.label : '')
          .attr('data-value', averageLine.xVal)
          .attr('class', this.averageLineClassName.line)
          .attr('x1', () => x)
          .attr('x2', () => x)
          .attr('y1', 0)
          .attr('y2', this.height_)
          .attr('stroke', averageLine.stroke)
          .attr('stroke-dasharray', averageLine.strokeDasharray)
          .attr('stroke-width', averageLine.strokeWidth);

        if (this.showAverageLineValueLabel) {
          const $wrapperAverageLineText = this.$xAxisWrapper.append('g')
            .attr('class', this.averageLineClassName.wrapperText);
          $wrapperAverageLineText.append('text')
            .attr('class', this.averageLineClassName.text)
            .attr('x', d => {
              return getBarTextX(this.xScale, averageLine.xVal);
            })
            .attr('y', d => {
              return -5;
            })
            // .attr('text-anchor', 'middle')
            .attr('text-anchor', averageLine.order === 0 ? 'end' : (averageLine.order === 1 ? 'start' : 'middle'))
            .attr('dy', '1.6em')
            .attr('fill', 'black')
            .text(d => {
              return `${averageLine.xVal}`;
            });
        }

        if (averageLine.label) {
          const tipLineHover = addTooltip.call(this, this.vis[0], this.vis[0].selectAll('.average-line'), function (event, d) {
            const lineLabel = event.currentTarget.getAttribute('data-label');
            const lineValue = event.currentTarget.getAttribute('data-value');
            return `${lineLabel} : ${lineValue}`;
          }, 'line-hover');
          this.tipObjs.push(tipLineHover);
        }
      }
    },
    initialize: function () {
      this.fetchData();
    }
  }
};
</script>
<style>
@import "../chart-services/css/main.css";

.bar-text {
  font-family: "Roboto", sans-serif;
  font-size: 12px;
}
.bar-line-text {
  font-family: "Roboto", sans-serif;
  font-size: 9px;
}
.bar-text-child {
  fill: #8f8f8f;
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
}
.zeroText {
  fill: #383838;
  font-size: 12px;
  font-family: "Roboto", sans-serif;
}
.bar-hover {
  border: 1px solid #ffffff;
  box-sizing: border-box;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.15);
  stroke: #ffffff;
  stroke-width: 1;
}
.rect-overlay {
  fill: transparent;
}
.tick text.selected-country {
  fill: black;
  font-weight: bold;
}
.tooltip-ul {
  list-style-type: none; /* Remove bullets */
  padding: 0; /* Remove padding */
}
.tooltip-bubble {
  height: 15px;
  width: 15px;
  border-radius: 50%;
  float: left;
  margin-right: 5px;
  margin-top: 3px;
}
.tooltip-box-container {
  display: flow-root;
}
.tooltip-text-label {
  float: left;
}
.tooltip-text-value {
  float: right;
}
.tooltip-text-space {
  width: 30px;
  float: left;
}
.bar, .tick {
  cursor: pointer;
}
</style>
